import InfoTooltip from "components/Tooltip/InfoTooltip";
import React, { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import csvIcon from "../../assets/img/icons/csv.svg";
import deleteIcon from "../../assets/img/icons/delete-3.svg";
import excelIcon from "../../assets/img/icons/excel.svg";
import fileIcon from "../../assets/img/icons/file.svg";
import pdfIcon from "../../assets/img/icons/pdf.svg";
import Plus from "../../assets/img/icons/plus_primary.svg";
import { DeleteButton } from "../../styledComponents/buttons";
import {
  CommonFlex,
  CommonText,
  InputLabel,
  InputWithLabelSection,
  PageTitle,
} from "../../styledComponents/common";
import DocumentPreviewModal from "./DocumentPreviewModal";

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const DocumentBox = styled.div`
  position: relative;
  height: 120px;
  border: 1px solid #c2cce1;
  border-radius: 8px;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  background-color: white;

  /* show delete button only on hover */
  &:hover .delete-btn {
    display: flex;
  }
`;

const DocumentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DocumentName = styled.div`
  font-size: 12px;
  color: #333;
  text-align: left;
  white-space: normal;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 4px;
  font-weight: 500;
`;

const UploadGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 15px;
`;

const UploadBox = styled.div`
  height: 120px;
  border: 1px dashed #c2cce1;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: #f8f9fb;
  transition: all 0.2s;

  &:hover {
    border-color: #6776cb;
    background-color: #f0f2f8;
  }
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #000;
`;

const AddButton = styled.button`
  background: #fff0e6;
  border: 1px solid #ff7a00;
  border-radius: 4px;
  color: #ff7a00;
  padding: 8px 16px;
  font-weight: 500;
  cursor: pointer;
  font-size: 14px;
  outline: none;
  transition: all 0.2s;

  &:hover {
    background: #ffe0cc;
  }
`;

const DocumentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DocumentCard = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #ffffff;
  height: 60px;
  justify-content: space-between;
  cursor: pointer;
  transition: border-color 0.2s;

  &:hover {
    border-color: #ff7a00;
  }
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  overflow: hidden;
  margin-right: 10px;
`;

const FileName = styled.span`
  font-size: 14px;
  color: #000000;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DeleteIconContainer = styled.div`
  width: 28px;
  height: 28px;
  min-width: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: #ffe5e5;
  border-radius: 4px;

  &:hover {
    background: #ffcccc;
  }
`;

const FileIcon = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fb;
  padding: 10px;
  text-align: center;
  position: relative;
  z-index: 0;
`;

/* full-box absolute image so it doesn't get pushed by the delete button */
const PreviewAbsolute = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
`;

/* wrapper for delete button - absolute positioned and hidden by default */
const DeleteWrapper = styled.div`
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 5;
  display: none; /* shown on hover via DocumentBox &:hover .delete-btn */
`;

/* clickable transparent overlay so embedded PDFs/iframes don't block clicks */
const ClickOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 4; /* below delete button (z-index:5) but above embedded content */
  background: transparent;
  cursor: pointer;
`;

// keep PreviewImage for any inline usages (not used for absolute images)
const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const DocumentUploadSection = ({
  documents,
  setDocuments,
  style,
  mode = "create", // "create" | "edit"
  pageType, // "detail" for read-only view
  orgId,
  jobId,
  createJobDocument,
  deleteJobDocument,
  documentError,
}) => {
  const isDetailView = pageType === "detail";
  const { t } = useTranslation();
  const [previewFile, setPreviewFile] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);
  const [uploadingFileNames, setUploadingFileNames] = useState(new Set());

  /**
   * Extract clean filename from document URL (removes query parameters and decodes)
   * @param {string} url - Document URL
   * @returns {string} Clean filename
   */
  const getCleanFileName = (url) => {
    if (!url) return "";
    try {
      // Get the last part of the path (after last /)
      const parts = url.split("/");
      const fileNameWithQuery = parts[parts.length - 1];
      // Remove query parameters (everything after ?)
      const fileName = fileNameWithQuery.split("?")[0];
      // Decode URL encoding (e.g., %20 -> space)
      return decodeURIComponent(fileName);
    } catch (error) {
      console.error("Error extracting filename:", error);
      return url.split("/").pop()?.split("?")[0] || "";
    }
  };

  const onDrop = (acceptedFiles, fileRejections) => {
    // Handle accepted files
    if (acceptedFiles && acceptedFiles.length > 0) {
      const newFiles = acceptedFiles.map(file =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        }),
      );

      if (
        mode === "edit" &&
        typeof createJobDocument === "function" &&
        orgId &&
        jobId
      ) {
        // upload immediately for edit mode
        newFiles.forEach(file => {
          // Track this file as uploading
          setUploadingFileNames(prev => new Set([...prev, file.name]));

          // Create FormData for file upload
          const formData = new FormData();
          formData.append("document", file); // Backend expects 'document' field name

          createJobDocument({
            organization_id: orgId,
            job_id: jobId,
            data: formData,
          });
        });
        // Optimistically show files
        if (setDocuments) setDocuments([...(documents || []), ...newFiles]);
      } else if (setDocuments) {
        // create mode: just update local state
        setDocuments([...(documents || []), ...newFiles]);
      }
    }

    // Handle rejected files and show user-friendly error messages
    if (fileRejections && fileRejections.length > 0) {
      const messages = fileRejections.map(rejection => {
        const { file, errors } = rejection;
        // If file is too large, show specific 10MB message
        const sizeError = errors.find(e => e.code === "file-too-large");
        if (sizeError) return `${file.name}: Maximum File Size is 10MB`;

        // If any error is about invalid type, show a clear message
        const invalidType = errors.find(e => e.code === "file-invalid-type");
        if (invalidType) return `${file.name}: ${t("file_type_not_supported")}`;

        // Fallback to the first error message or code
        const firstError = errors[0];
        return `${file.name}: ${firstError.message || firstError.code}`;
      });

      setErrorMessages(messages);

      // Clear error messages after 5 seconds
      setTimeout(() => setErrorMessages([]), 5000);
    }
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    // Restrict to only images (jpg, jpeg, png) and PDFs
    accept: {
      "image/*": [".jpeg", ".jpg", ".png"],
      "application/pdf": [".pdf"],
    },
    maxSize: 10 * 1024 * 1024, // 10MB
  });

  const removeDocument = (e, idOrIndex) => {
    e.stopPropagation();
    if (!documents || !documents.length) return;

    // Edit mode: use document id and dispatch delete
    if (mode === "edit" && deleteJobDocument && orgId && jobId) {
      const docIdx =
        typeof idOrIndex === "number"
          ? idOrIndex
          : documents.findIndex(d => d.id === idOrIndex);
      if (docIdx === -1) return;
      const doc = documents[docIdx];
      if (doc && doc.preview) {
        URL.revokeObjectURL(doc.preview);
      }
      if (doc?.id) {
        deleteJobDocument({
          organization_id: orgId,
          job_id: jobId,
          document_id: doc.id,
        });
      }
      if (setDocuments) {
        const newDocs = [...documents];
        newDocs.splice(docIdx, 1);
        setDocuments(newDocs);
      }
      return;
    }

    // Create mode: remove locally by index
    const index =
      typeof idOrIndex === "number"
        ? idOrIndex
        : documents.findIndex((_, i) => i === idOrIndex);
    if (index === -1) return;
    const newDocs = [...documents];
    if (newDocs[index]?.preview) {
      URL.revokeObjectURL(newDocs[index].preview);
    }
    newDocs.splice(index, 1);
    if (setDocuments) setDocuments(newDocs);
  };

  const handlePreview = file => {
    // For detail view, ensure file has preview URL
    const fileForPreview =
      isDetailView && file.document && !file.preview
        ? { ...file, preview: file.document }
        : file;
    setPreviewFile(fileForPreview);
    setModalOpen(true);
  };

  const toggleModal = () => setModalOpen(!modalOpen);

  const getFileIcon = file => {
    // For detail view, check document URL extension if type is not available
    if (!file.type && file.document) {
      const url = file.document.toLowerCase();
      if (
        url.includes(".jpg") ||
        url.includes(".jpeg") ||
        url.includes(".png")
      ) {
        return file.preview || file.document;
      }
      if (url.includes(".pdf")) return pdfIcon;
      if (url.includes(".xls") || url.includes(".xlsx")) return excelIcon;
      if (url.includes(".csv")) return csvIcon;
    }
    if (!file.type) return fileIcon;
    if (file.type.startsWith("image/")) {
      return file.preview || file.document; // Use the preview or document URL for images
    }

    if (file.type.includes("pdf")) return pdfIcon;
    if (file.type.includes("sheet") || file.type.includes("excel"))
      return excelIcon;
    if (file.type.includes("csv")) return csvIcon;

    return fileIcon;
  };

  const renderPreview = file => {
    const previewUrl = file.preview || file.document;

    // Image preview: full absolute image
    if (file.type && file.type.startsWith("image/")) {
      return <PreviewAbsolute src={previewUrl} alt={file.name} />;
    }

    // For detail view, check document URL extension if type is not available
    if (!file.type && file.document) {
      const url = file.document.toLowerCase();
      if (
        url.includes(".jpg") ||
        url.includes(".jpeg") ||
        url.includes(".png")
      ) {
        return <PreviewAbsolute src={previewUrl} alt={file.name} />;
      }
    }

    // PDF preview: embed PDF in the box (object fallback to iframe if needed)
    if (
      (file.type && file.type.includes("pdf")) ||
      (file.document && file.document.toLowerCase().includes(".pdf"))
    ) {
      return (
        <iframe
          src={previewUrl}
          title={file.name}
          style={{ width: "100%", height: "100%", border: "none" }}
          allow="fullscreen"
          allowFullScreen
        />
      );
    }

    // Other file types: show icon + filename centered
    let iconSrc = fileIcon;
    if (file.type && file.type.includes("sheet")) iconSrc = excelIcon;
    else if (file.type && file.type.includes("excel")) iconSrc = excelIcon;
    else if (file.type && file.type.includes("csv")) iconSrc = csvIcon;
    else if (file.type && file.type.includes("pdf")) iconSrc = pdfIcon;

    return (
      <FileIcon>
        <img
          src={iconSrc}
          alt="file type"
          width="32px"
          style={{ marginBottom: "8px", zIndex: 1 }}
        />
        <CommonText
          fontSize="10px"
          margin="0"
          style={{ wordBreak: "break-all", lineHeight: "1.2", zIndex: 1 }}
        >
          {file.name}
        </CommonText>
      </FileIcon>
    );
  };

  useEffect(() => {
    // Cleanup object URLs on unmount
    return () => {
      documents?.forEach(file => {
        if (file.preview) URL.revokeObjectURL(file.preview);
      });
    };
  }, []);

  // Handle API errors: remove optimistically added files if upload fails
  useEffect(() => {
    if (mode === "edit" && documentError && uploadingFileNames.size > 0) {
      // Remove any files that were being uploaded but failed
      if (setDocuments && documents) {
        const updatedDocuments = documents.filter(
          doc => !uploadingFileNames.has(doc.name),
        );
        setDocuments(updatedDocuments);
      }
      // Clear the uploading files set
      setUploadingFileNames(new Set());
    }
  }, [documentError, mode]);

  return (
    <InputWithLabelSection style={style}>
      <CommonFlex gap="8px" alignItems="center">
        <PageTitle fontSize="16px">{t("documents")}</PageTitle>
        <InfoTooltip toolTipText="Only PDF and image files (jpg, jpeg, png) can be uploaded. Maximum Size is: 10MB" />
      </CommonFlex>

      <UploadGrid>
        {documents.map((file, index) => {
          // For detail view, format document URL for preview
          const fileForPreview =
            isDetailView && file.document
              ? {
                  ...file,
                  preview: file.document,
                  name: file.name || getCleanFileName(file.document) || `Document ${index + 1}`,
                }
              : file;

          return (
            <DocumentWrapper key={file.id || index}>
              <DocumentBox title={fileForPreview.name || file.name}>
                {!isDetailView && (
                  <DeleteWrapper className="delete-btn">
                    <DeleteButton
                      onClick={e => removeDocument(e, file.id ?? index)}
                      aria-label={`Delete ${fileForPreview.name || file.name}`}
                    >
                      <img src={deleteIcon} alt="delete" width="14px" />
                    </DeleteButton>
                  </DeleteWrapper>
                )}

                {renderPreview(fileForPreview)}

                {/* overlay captures clicks for preview (works for images, PDFs, iframes) */}
                <ClickOverlay
                  role="button"
                  tabIndex={0}
                  onClick={() => handlePreview(file)}
                  onKeyDown={e => {
                    if (e.key === "Enter" || e.key === " ") handlePreview(file);
                  }}
                  aria-label={`Open preview for ${file.name}`}
                />
              </DocumentBox>
              <DocumentName title={fileForPreview.name || file.name}>
                {fileForPreview.name ||
                  file.name ||
                  getCleanFileName(file.document) ||
                  `Document ${index + 1}`}
              </DocumentName>
            </DocumentWrapper>
          );
        })}

        {!isDetailView && (
          <UploadBox
            {...getRootProps({ onClick: open })}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                open();
              }
            }}
          >
            <input {...getInputProps()} />
            <img src={Plus} alt="add" width="24px" />
            <CommonText fontSize="12px" color="#6776cb" margin="8px 0 0 0">
              {t("add_file")}
            </CommonText>
          </UploadBox>
        )}
      </UploadGrid>

      {/* Error messages for rejected files */}
      {errorMessages?.length > 0 && (
        <div style={{ marginTop: 12 }}>
          {errorMessages.map((msg, i) => (
            <CommonText key={i} fontSize="12px" color="#d32f2f" margin="4px 0">
              {msg}
            </CommonText>
          ))}
        </div>
      )}

      <DocumentPreviewModal
        isOpen={modalOpen}
        toggle={toggleModal}
        file={previewFile}
      />
    </InputWithLabelSection>
  );

  // return (
  //   <InputWithLabelSection style={style}>
  //     <div {...getRootProps()}>
  //       <input {...getInputProps()} />

  //       <HeaderContainer>
  //         <Title>{t('documents')}</Title>
  //         <AddButton type="button" onClick={open}>
  //           {t('add_file')}
  //         </AddButton>
  //       </HeaderContainer>

  //       <DocumentsGrid>
  //         {documents?.length>0 && documents.map((file, index) => (
  //           <DocumentCard key={index} onClick={() => handlePreview(file)}>
  //             <FileInfo>
  //               <IconImage src={getFileIcon(file)} alt="file type" />
  //               <FileName title={file.name}>{file.name}</FileName>
  //             </FileInfo>
  //             <DeleteIconContainer onClick={(e) => removeDocument(e, index)}>
  //               <img src={deleteIcon} alt="delete" width="14px" />
  //             </DeleteIconContainer>
  //           </DocumentCard>
  //         ))}
  //       </DocumentsGrid>
  //     </div>

  //     <DocumentPreviewModal
  //       isOpen={modalOpen}
  //       toggle={toggleModal}
  //       file={previewFile}
  //     />
  //   </InputWithLabelSection>
  // );
};

export default DocumentUploadSection;
